`
<template>
    <div>
        <div class="main" :class="communication === PLAYER_COMMUNICATION.INLINE ? 'is-inline' : null">
            <ChannelItemAudioPlayer
                ref="channel_audio"
                :debug="debug"
                :communication="communication"
                :muted="muted"
                @transition-start="transitionStartStarted(CHANNELS.AUDIO, $event)"
                @transition-end="transitionChangedFinished(CHANNELS.AUDIO, $event)"
            />

            <ChannelItemMediaPlayer
                ref="channel_media"
                v-if="!debug"
                :debug="debug"
                :communication="communication"
                :muted="muted"
                @transition-start-started="transitionStartStarted(CHANNELS.MEDIA, $event)"
                @transition-end-ended="transitionEndEnded(CHANNELS.MEDIA)"
                @transition-change-finished="transitionChangedFinished(CHANNELS.MEDIA, $event)"
            />
        </div>

        <div v-if="debug" style="font-size: 12px; z-index: 1000; padding: 50px;">
            <h1>Debug</h1>
            <div style="display: flex; width: 100%;">
                <pre style="width: 100%">{{ debugDurations(durations) }}</pre>
                Media duration
                <pre style="width: 100%">{{ activeMedia?.duration }}</pre>
                Audio duration
                <pre style="width: 100%">{{ activeAudio?.duration }}</pre>
            </div>

            <ul>
                <li>Channel media: {{ channels[CHANNELS.MEDIA].activeItemId }}</li>
                <li>Channel audio: {{ channels[CHANNELS.AUDIO].activeItemId }}</li>
                <li>Last channel media: {{ channels[CHANNELS.MEDIA].lastItemId }}</li>
                <li>Last channel audio: {{ channels[CHANNELS.AUDIO].lastItemId }}</li>
                <li>Render state: current: {{ renderState }}, last: {{ lastRenderState }}</li>
            </ul>
            <div style="display: flex;">
                <div style="width: 33%">
                    <h3>Media</h3>
                    <div v-for="item in channels[CHANNELS.MEDIA].items" :key="item.id" style="display: flex; align-items: center">
                        <img v-if="item.type === MEDIA_TYPES.IMAGE" :src="item.src" style="width: 200px" />
                        <pre>{{ debugItem(item) }}</pre>
                    </div>

                    <div v-if="channels[CHANNELS.MEDIA].items.length <= 0">
                        No media
                    </div>

                    <h3>Audio</h3>
                    <div v-for="item in channels[CHANNELS.AUDIO].items" :key="item.id">
                        <pre>{{ debugItem(item) }}</pre>
                    </div>

                    <div v-if="channels[CHANNELS.AUDIO].items.length <= 0">
                        No audio
                    </div>
                </div>

                <div style="width: 33%">
                    <h3>Active media</h3>
                    <div>
                        <ChannelItemMediaPlayer
                            ref="channel_media"
                            :debug="debug"
                            :communication="communication"
                            :muted="muted"
                            @transition-start-started="transitionStartStarted(CHANNELS.MEDIA, $event)"
                            @transition-end-ended="transitionEndEnded(CHANNELS.MEDIA)"
                            @transition-change-finished="transitionChangedFinished(CHANNELS.MEDIA, $event)"
                        />

                        <pre v-if="activeMedia">{{ debugItem(activeMedia) }}</pre>
                    </div>
                    <div v-if="!activeMedia">
                        No active media
                    </div>

                    <h3>Active audio</h3>
                    <div v-if="activeAudio">
                        <pre>{{ debugItem(activeAudio) }}</pre>
                    </div>
                    <div v-else>
                        No active audio
                    </div>
                </div>

                <div style="width: 33%">
                    <h3>Commands</h3>
                    <pre>{{ debugData.commands }}</pre>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

    import FileMixin from '../../mixins/FileMixin';
    import { EventBus } from '../../utils/eventBus';
    import { getMaxDurationFromBlock } from '../../utils/getMaxDurationForBlock';
    import { getDurationPerImage } from '../../utils/duration';
    import { CHANNELS, CONTROLS, BACKGROUND_TYPE, MEDIA_TYPES, RENDER_STATES, DEFAULT_TRANSITION_TIME, TRANSITION_TYPE, DURATION_STATES, TIMER_STATES, BLOCK_TYPES } from '../enums';
    import ChannelItemAudioPlayer from './ChannelItemAudioPlayer.vue';
    import ChannelItemMediaPlayer from './ChannelItemMediaPlayer.vue';
    import TRANSITIONS from '../../player-v2/components/transitions';
    import { PLAYER_COMMUNICATION } from '../../enums/player-communication';
    import { mapState } from 'vuex';

    export default {
        components: { ChannelItemMediaPlayer, ChannelItemAudioPlayer },

        props: {
            communication: {
                type: String,
                default: PLAYER_COMMUNICATION.WEBSOCKET,
            },
            debug: {
                type: Boolean,
                default: false,
            },

            readonly: {
                type: Boolean,
                default: false,
            },

            muted: {
                type: Boolean,
                default: false,
            },
        },

        mixins: [
            FileMixin,
        ],

        created() {

            switch (this.communication) {
                case PLAYER_COMMUNICATION.WEBSOCKET:
                    EventBus.$on('WebsocketReconnect', this.setupWebsocketEvents);

                    this.setupWebsocketEvents();
                    break;
                case PLAYER_COMMUNICATION.INLINE:
                    this.setupInlineEvents();
                    break;
                case PLAYER_COMMUNICATION.IPC:
                    this.setupIpcEvents();
                    break;
            }

            this.durationIntervalId = setInterval(() => {
                // Div does not support resize event so we check every time
                this.executeChannelItemMethod('resizeInterval', [CHANNELS.MEDIA]);

                const currentDuration = this.currentDuration;

                if (!currentDuration) {
                    return;
                }

                if (this.activeMediaBlock?.ceremony?.type === 'ledwall') {
                    return;
                }

                if (currentDuration.state === DURATION_STATES.IN_ACTIVE) {
                    return;
                }

                if (this.activeMedia && this.activeMedia.duration.timerState !== TIMER_STATES.INIT) {
                    this.activeMedia.duration.timer += 0.1;
                }

                if (this.activeAudio && this.activeAudio.duration.timerState !== TIMER_STATES.INIT) {
                    this.activeAudio.duration.timer += 0.1;
                }

                this.currentDuration.progressBar.milliSeconds += 0.1;

                // Keep the image block active when nearly ending
                let keepActive = (
                    currentDuration.progressBar.milliSeconds >= currentDuration.progressBar.max - 1 - (this.activeMedia?.duration?.endDuration || 0) &&
                    this.activeMedia?.type === MEDIA_TYPES.IMAGE
                );

                if (Math.floor(currentDuration.progressBar.milliSeconds) !== currentDuration.progressBar.seconds) {
                    this.currentDuration.progressBar.seconds++;

                    this.updateDuration(currentDuration);

                }

                this.handleDurationForChannel(CHANNELS.MEDIA, keepActive);
                this.handleDurationForChannel(CHANNELS.AUDIO);

                // Send signal we are finished
                if (currentDuration.progressBar.milliSeconds >= currentDuration.progressBar.max) {
                    this.finishActiveBlock();
                }
            }, 100);
        },

        computed: {
            PLAYER_COMMUNICATION() {
                return PLAYER_COMMUNICATION;
            },

            streamingConfig() {
                return this.companySettings?.streaming || window.PLAYER_STREAMING;
            },

            activeMedia() {
                const activeItemId = this.channels[CHANNELS.MEDIA].activeItemId;

                if (activeItemId === null) {
                    return null;
                }

                return this.channels[CHANNELS.MEDIA].items.find(item => item.id === activeItemId);
            },

            activeAudio() {
                const activeItemId = this.channels[CHANNELS.AUDIO].activeItemId;

                if (activeItemId === null) {
                    return null;
                }

                return this.channels[CHANNELS.AUDIO].items.find(item => item.id === activeItemId);
            },

            activeMediaBlock() {
                return this.activeMedia?.block || null;
            },

            currentDuration() {
                return this.durations.find(duration => duration.id === this.currentDurationId);
            },

            ...mapState({
                companySettings: state => state.companySettings,
            }),
        },

        data() {
            return {
                MEDIA_TYPES,
                RENDER_STATES,
                CHANNELS,

                currentDurationId: null,
                durations: [],
                durationIntervalId: null,
                latestId: 1, // Start with 1

                channels: {
                    [CHANNELS.MEDIA]: {
                        items: [],
                        activeItemId: null,
                        lastItemId: null,
                    },
                    [CHANNELS.AUDIO]: {
                        items: [],
                        activeItemId: null,
                        lastItemId: null,
                    },
                },

                lastRenderState: RENDER_STATES.NONE,
                renderState: RENDER_STATES.NONE,

                debugData: {
                    commands: [],
                },
            };
        },

        methods: {
            /**
             * When do we need to change to the next channel item
             *
             * @param channelType
             */
            handleDurationForChannel(channelType, keepActive = false) {
                const activeItemId = this.channels[channelType].activeItemId;
                const activeItem = this.channels[channelType].items.find(item => item.id === activeItemId);

                if (!activeItem || activeItem.control !== CONTROLS.AUTO || activeItem.repeat || activeItem.duration.total === null || keepActive) {
                    return;
                }

                if (activeItem.duration.timer >= activeItem.duration.contentAt && activeItem.duration.timerState === TIMER_STATES.START) {
                    activeItem.duration.timerState = TIMER_STATES.CONTENT;
                }

                if (activeItem.duration.timer >= activeItem.duration.endAt) {
                    const nextChannelItemId = this.getNextChannelItem(channelType);

                    this.gotoChannelItem(channelType, nextChannelItemId);
                }
            },

            /**
             * Set up the event bus for the web preview
             */
            setupInlineEvents() {
                EventBus.$on('BlockStarted', this.blockStartedEvent);
                EventBus.$on('BlockStateChanged', this.blockStateChangedEvent);
                EventBus.$on('BlockActiveFileChanged', this.blockActiveFileChangedEvent);
                EventBus.$on('CeremonyFinished', this.ceremonyFinishedEvent);
            },

            setupIpcEvents() {
                window.addEventListener('ipc-from-controller', this.ipcFromControllerEvents);
            },

            /**
             * Set up the websockets for the player desktop application
             */
            setupWebsocketEvents() {
                window.stopListening();

                const channel = Echo.channel('blockmessages');

                channel.listen('PreloadFiles', (event) => {
                    for (const file of event.files) {
                        const existing = this.$root.preloadQueue.getItem(file);

                        if (existing) {
                            continue;
                        }

                        this.$root.preloadQueue.loadFile(file);

                        console.log('[general] Adding file for preload: ' + file);
                    }
                });

                channel.listen('BlockStarted', this.blockStartedEvent);
                channel.listen('BlockStateChanged', this.blockStateChangedEvent);
                channel.listen('BlockActiveFileChanged', this.blockActiveFileChangedEvent);
                channel.listen('CeremonyFinished', this.ceremonyFinishedEvent);
                channel.listen('VolumeUpdated', this.volumeUpdated);
            },

            ipcFromControllerEvents(event) {
                const detail = event.detail;

                switch (detail.action) {
                    case 'BlockStarted':
                        return this.blockStartedEvent(detail.data);
                    case 'BlockStateChanged':
                        return this.blockStateChangedEvent(detail.data);
                    case 'BlockActiveFileChanged':
                        return this.blockActiveFileChangedEvent(detail.data);
                    case 'CeremonyFinished':
                        return this.ceremonyFinishedEvent(detail.data);
                }
            },

            /**
             * Create the media and audio for the started block
             *
             * @param event
             */
            async blockStartedEvent(event) {
                this.cleanupDurations();

                const block = event.block;
                const isFixedBlock = block ? block.settings.find(setting => setting.name === 'fixed_block' && setting.value === '1') : false;
                const initialTransitionTime = event.initial_transition_time !== null && event.initial_transition_time !== undefined ? event.initial_transition_time : null;
                const eventTransitionTime = event.default_transition_time !== null && event.default_transition_time !== undefined ? event.default_transition_time : null;

                const transitionType = (block.settings || []).find(setting => setting.name === 'transition_type')?.value || TRANSITION_TYPE.CROSS_FADE;
                const transition = this.getTransitionType(transitionType);
                const shouldWaitForTransitionEnd = transition.shouldWaitForTransitionEnd();

                let transitionStart = eventTransitionTime !== null ? eventTransitionTime : DEFAULT_TRANSITION_TIME;
                let transitionEnd = eventTransitionTime !== null ? eventTransitionTime : DEFAULT_TRANSITION_TIME;

                const file = event.file;
                const blockToPlay = block.parent_block ? block : {
                    ...(block.subblocks.length ? block.subblocks[0] : block),
                    parent_block: block,
                    ceremony: block.ceremony,
                };

                console.log('[general] Block started', blockToPlay.id, blockToPlay.name, transitionType, blockToPlay);

                if (file) {
                    console.log('[general] Initial file', file.id, file.name, file);
                }

                const mediaOptions = {
                    isFixedBlock,
                    shouldWaitForTransitionEnd,
                    shouldRepeat: event.should_repeat ||
                        this.shouldRepeatMedia(blockToPlay) ||
                        (blockToPlay.use_files_from_block && this.shouldRepeatMedia(blockToPlay.use_files_from_block)),
                    shouldRepeatViaController: event.should_repeat || false,
                    initialFileId: file?.id || null,
                    transitionStart,
                    transitionEnd,
                    transitionType,
                    muted: this.shouldMuteMedia(blockToPlay),
                };

                const audioOptions = {
                    isFixedBlock,
                    shouldWaitForTransitionEnd,
                    shouldRepeat: event.should_repeat || this.shouldRepeatAudio(blockToPlay, mediaOptions),
                    shouldRepeatViaController: event.should_repeat || false,
                    transitionStart,
                    transitionEnd,
                    transitionType,
                };

                const durationId = this.getUniqueId('duration');

                // Build media and audio
                const media = this.getBlockMediaChannelItems(blockToPlay, durationId, mediaOptions);
                const audio = this.getBlockAudioChannelItems(blockToPlay, durationId, audioOptions);

                this.setChannelItems(media, CHANNELS.MEDIA, mediaOptions);
                this.setChannelItems(audio, CHANNELS.AUDIO, audioOptions);

                this.durations.push({
                    id: durationId,
                    block,
                    state: DURATION_STATES.IN_ACTIVE,

                    progressBar: {
                        milliSeconds: 0,
                        seconds: 0,
                        max: this.getMaxDurationFromBlock(block, mediaOptions, audioOptions),
                    },
                });
                this.currentDurationId = durationId;

                await Promise.all([
                    this.startMedia(media, mediaOptions, blockToPlay, initialTransitionTime),
                    this.startAudio(audio, initialTransitionTime),
                ]);

                this.play(!media.length && !audio.length, this.getStartedInitialRenderState(media, audio));
            },

            async startMedia(media, mediaOptions, blockToPlay, initialTransitionTime) {
                console.log('[media] Start media:', media);

                if (mediaOptions.initialFileId) {
                    const mediaItem = this.channels[CHANNELS.MEDIA].items.find(item => {
                        return item.block.id === blockToPlay.id && item.fileId === mediaOptions.initialFileId;
                    });

                    await this.gotoChannelItem(CHANNELS.MEDIA, mediaItem?.id, true, initialTransitionTime !== null ? {
                        transitionStart: initialTransitionTime,
                        transitionEnd: initialTransitionTime,
                    } : null);
                } else {
                    if (this.channels[CHANNELS.MEDIA].activeItemId === null && media.length) {
                        const channelItemId = media.length ? media[0].id : null;

                        await this.gotoChannelItem(CHANNELS.MEDIA, channelItemId, true, initialTransitionTime !== null ? {
                            transitionStart: initialTransitionTime,
                            transitionEnd: initialTransitionTime,
                        } : null);
                    } else {
                        const nextChannelItemId = this.getNextChannelItem(CHANNELS.MEDIA, true);

                        await this.gotoChannelItem(CHANNELS.MEDIA, nextChannelItemId, true, initialTransitionTime !== null ? {
                            transitionStart: initialTransitionTime,
                            transitionEnd: initialTransitionTime,
                        } : null);
                    }
                }
            },

            async startAudio(audio, initialTransitionTime) {
                console.log('[audio] Start audio:', audio);

                if (this.channels[CHANNELS.AUDIO].activeItemId === null && audio.length) {
                    const channelItemId = audio.length ? audio[0].id : null;

                    await this.gotoChannelItem(CHANNELS.AUDIO, channelItemId, true);
                } else {
                    const nextChannelItemId = this.getNextChannelItem(CHANNELS.AUDIO, true);

                    // Update the transition start for a user defined transition time cut
                    if (initialTransitionTime !== null && nextChannelItemId) {
                        this.updateChannelItem(CHANNELS.AUDIO, nextChannelItemId, {
                            transitionStart: initialTransitionTime,
                        });
                    }

                    await this.gotoChannelItem(CHANNELS.AUDIO, nextChannelItemId, true);
                }
            },

            getStartedInitialRenderState(media, audio) {
                if (!media.length && audio.length) {
                    return RENDER_STATES.AUDIO_ONLY;
                }

                return RENDER_STATES.FULL;
            },

            getMaxDurationFromBlock(block, mediaOptions, audioOptions) {
                if (mediaOptions.shouldRepeatViaController || audioOptions.shouldRepeatViaController) {
                    return 60 * 60 * 4;
                }

                return getMaxDurationFromBlock(block.ceremony, block);
            },

            /**
             * Should we repeat the audio
             *
             * @param block
             * @param options
             */
            shouldRepeatAudio(block, options) {
                const repeatAudio = block.parent_block.settings.find(setting => setting.name === 'repeat_audio');
                const isFixedBlock = block.parent_block.settings.find(setting => setting.name === 'fixed_block' && setting.value === '1');

                if (repeatAudio && repeatAudio.value === '0') {
                    return false;
                }

                return repeatAudio?.value === '1' || !!isFixedBlock || options.shouldRepeat;
            },

            /**
             * Should we repeat the media
             *
             * @param block
             */
            shouldRepeatMedia(block) {
                const shouldRepeatVideo = block.settings.find(setting => setting.name === 'repeat_videos' && setting.value === '1');
                const shouldRepeatPhotos = block.settings.find(setting => setting.name === 'repeat_photos' && setting.value === '1');
                const isFixed = block.parent_block.settings.find(setting => setting.name === 'fixed_block' && setting.value === '1');

                return !!shouldRepeatVideo || !!shouldRepeatPhotos || !!isFixed;
            },

            shouldMuteMedia(block) {
                // Mute the video when there are songs
                if (block.type === BLOCK_TYPES.VIDEO && block.parent_block.files.length) {
                    return true;
                }

                return false;
            },

            /**
             * Get all the media in the block
             *
             * @param block
             * @param durationId
             * @param options
             */
            getBlockMediaChannelItems(block, durationId, options) {
                if (block.type === BLOCK_TYPES.CALL) {
                    if (!this.streamingConfig) {
                        return [];
                    }

                    const mediaItem = this.getMediaChannelItemFromFile(block, durationId, null, options);
                    mediaItem.src = this.streamingConfig.streaming + '/live/' + block.livestream;

                    mediaItem.streamingConfig = {
                        token: 'ceremony:' + block.livestream,
                        apiUrl: this.streamingConfig.api,
                    };

                    mediaItem.control = CONTROLS.MANUAL;

                    return [
                        mediaItem,
                    ];
                }

                if (block.ceremony.type === 'ledwall' && !options.transitionStart && !options.transitionEnd) {
                    const mediaItem = this.getMediaChannelItemFromFile(block, durationId, block.files[0], options);
                    mediaItem.repeat = true;

                    return block.files.length ? [
                        mediaItem,
                    ] : [];
                }

                const media = block.files.filter(file => {
                    return !!this.getMediaType(block, file);
                }).map(file => {
                    return this.getMediaChannelItemFromFile(block, durationId, file, options);
                });

                // For repeating we mark the last item to start the index back to 0
                if (options.shouldRepeat && media.length) {
                    media[media.length - 1].nextActiveItemId = media[0].id;
                }

                // For non-repeating images and fixed duration per image we need to set the last media duration to the remaining length
                const isFixedDurationPerItem = !!block.settings.find(setting => setting.name === 'photo_calculation' && setting.value === 'custom');

                if (isFixedDurationPerItem && !options.shouldRepeat && media.length) {
                    // Find the duration of all the media
                    const mediaDuration = media.reduce((totalDuration, item, index) => {
                        // We want to skip the last item
                        if (index === media.length - 1) {
                            return totalDuration;
                        }

                        return totalDuration + item.duration.total;
                    }, 0);

                    media[media.length - 1].duration = this.buildChannelItemDuration(
                        Math.max(0, block.total_duration - mediaDuration),
                        media[media.length - 1].transitionStart,
                        media[media.length - 1].transitionEnd,
                        options.shouldWaitForTransitionEnd,
                    );
                }

                return media;
            },

            /**
             * Get all the audio in the block
             *
             * @param block
             * @param durationId
             * @param options
             */
            getBlockAudioChannelItems(block, durationId, options) {
                const audio = block.parent_block.files.filter(file => this.isAudio(file)).map(file => this.getAudioChannelItemFromFile(block, durationId, file, options));

                // For repeating we mark the last item to start the index back to 0
                if (options.shouldRepeat && audio.length) {
                    audio[audio.length - 1].nextActiveItemId = audio[0].id;
                }

                return audio;
            },

            /**
             * Build the media channel item
             *
             * @param block
             * @param durationId
             * @param file
             * @param options
             */
            getMediaChannelItemFromFile(block, durationId, file, options) {
                const item = this.getChannelItemFromFile(block, durationId, file, options);

                item.control = block.has_manual_action ? CONTROLS.MANUAL : CONTROLS.AUTO;
                item.background = this.getBackground(block, item);
                item.muted = options.muted || false;

                if (item.type === MEDIA_TYPES.VIDEO) {
                    const duration = Number(file.duration);

                    item.transitionStart = this.limitTransitionTimeToDuration(item.transitionStart, duration * 1000, 35);
                    item.transitionEnd = this.limitTransitionTimeToDuration(item.transitionEnd, duration * 1000, 35);

                    item.duration = this.buildChannelItemDuration(duration, item.transitionStart, item.transitionEnd, options.shouldWaitForTransitionEnd, MEDIA_TYPES.VIDEO);
                }

                if (item.type === MEDIA_TYPES.IMAGE && options.isFixedBlock) {
                    const duration = getMaxDurationFromBlock(block.ceremony, block);

                    item.transitionStart = this.limitTransitionTimeToDuration(item.transitionStart, duration * 1000, 35);
                    item.transitionEnd = this.limitTransitionTimeToDuration(item.transitionEnd, duration * 1000, 35);

                    item.duration = this.buildChannelItemDuration(duration, item.transitionStart, item.transitionEnd, options.shouldWaitForTransitionEnd, MEDIA_TYPES.IMAGE);

                }
                return item;
            },

            /**
             * Build the audio channel item
             *
             * @param block
             * @param durationId
             * @param file
             * @param options
             */
            getAudioChannelItemFromFile(block, durationId, file, options) {
                const item = this.getChannelItemFromFile(block, durationId, file, options);

                item.transitionStart = this.limitTransitionTimeToDuration(options.transitionStart, file.song.duration * 1000, 35);
                item.transitionEnd = this.limitTransitionTimeToDuration(options.transitionEnd, file.song.duration * 1000, 35);

                item.duration = this.buildChannelItemDuration(file.song.duration, item.transitionStart, item.transitionEnd, options.shouldWaitForTransitionEnd, null);

                return item;
            },

            buildChannelItemDuration(total, transitionStart, transitionEnd, shouldWaitForTransitionEnd, mediaType) {
                // Cross-fade
                if (!shouldWaitForTransitionEnd) {
                    const startDuration = transitionStart / 1000;
                    const endDuration = transitionEnd / 1000;

                    // We should probably introduce a second endAt for cross-fade transitions, but this will suffice for now.
                    if (mediaType === MEDIA_TYPES.IMAGE) {
                        total += endDuration / 2;
                    }

                    return {
                        startDuration,
                        content: total - startDuration - endDuration,
                        endDuration,

                        total,
                        contentAt: startDuration / 2,
                        endAt: total - (endDuration / 2),

                        timer: 0,
                        timerState: TIMER_STATES.START,
                    };
                }

                // Wait for end
                const startDuration = transitionStart / 1000;
                const endDuration = transitionEnd / 1000;

                return {
                    startDuration,
                    content: total - startDuration - endDuration,
                    endDuration,

                    total,
                    contentAt: startDuration,
                    endAt: total - endDuration,

                    timer: 0,
                    timerState: TIMER_STATES.START,
                };
            },

            /**
             * Build the channel item based on the block file
             *
             * @param block
             * @param durationId
             * @param file
             * @param options
             */
            getChannelItemFromFile(block, durationId, file, options) {
                const id = this.getUniqueId('item');
                const type = this.getMediaType(block, file);
                const duration = this.getChannelItemDuration(block, type, file);

                const item = {
                    id,
                    durationId,
                    type,
                    control: CONTROLS.AUTO,

                    options,

                    // Source data
                    src: file ? file.public_path.replace('/:code/', '/' + block.ceremony.code + '/') : null,

                    // Metadata
                    block,
                    fileId: file?.id || null,

                    // Transition data
                    transitionStart: this.limitTransitionTimeToDuration(options.transitionStart, duration * 1000, 35),
                    transitionEnd: this.limitTransitionTimeToDuration(options.transitionEnd, duration * 1000, 35),
                    transitionType: options.transitionType,

                    // Handle data
                    repeat: false,
                    nextActiveItemId: null,

                    // Started at
                    startedAt: null,
                };

                item.duration = this.buildChannelItemDuration(duration, item.transitionStart, item.transitionEnd, options.shouldWaitForTransitionEnd, type);

                return item;
            },

            /**
             * Go to the next item in the channel
             *
             * @param channelType
             * @param ignoreNextActiveItemId
             */
            getNextChannelItem(channelType, ignoreNextActiveItemId = false) {
                const channel = this.channels[channelType];
                const item = channel.items.find(item => item.id === channel.activeItemId);

                // Update the active index
                if (!ignoreNextActiveItemId && item && item.nextActiveItemId !== undefined && item.nextActiveItemId !== null) {
                    return item.nextActiveItemId;
                } else {
                    const currentItemIndex = channel.items.findIndex(item => item.id === channel.activeItemId);
                    let nextItemIndex = currentItemIndex + 1;

                    if (nextItemIndex >= channel.items.length) {
                        nextItemIndex = null;
                    }

                    if (nextItemIndex !== null && channel.items[nextItemIndex]) {
                        return channel.items[nextItemIndex].id;
                    }
                }

                return null;
            },

            /**
             * Go to specific channel item
             *
             * @param channelType
             * @param channelItemId
             * @param waitForTransition
             */
            async gotoChannelItem(channelType, channelItemId, waitForTransition = false, options = null, duration = 0) {
                return new Promise((resolve) => {
                    const activeItemIndex = this.channels[channelType].items.findIndex(item => item.id === this.channels[channelType].activeItemId);
                    const activeItem = this.channels[channelType].items[activeItemIndex];

                    const item = this.channels[channelType].items.find(item => item.id === channelItemId) || null;

                    if (item) {
                        item.duration.timer = duration;
                        item.duration.timerState = TIMER_STATES.INIT;
                    }

                    // Update the active id
                    this.channels[channelType].activeItemId = channelItemId;
                    this.executeChannelItemMethod('setActiveItem', [channelType], [
                        {
                            item,
                            duration,
                            options,
                        },
                    ]);

                    // Set the started at for the previous item, for new items it must be null (default)
                    if (activeItem && this.channels[channelType].items[activeItemIndex]) {
                        this.channels[channelType].items[activeItemIndex].startedAt = new Date();
                    }

                    if (activeItem && waitForTransition) {
                        this.channels[channelType].transitioning = true;

                        if (this.channels[channelType].transitioningTimeoutId) {
                            clearTimeout(this.channels[channelType].transitioningTimeoutId);
                        }

                        this.channels[channelType].transitioningTimeoutId = setTimeout(() => {
                            // Update the duration
                            this.channels[channelType].transitioning = false;
                            resolve();

                        }, activeItem.transitionEnd);

                        console.log('[' + channelType + '] Change ' + channelType + ' to ' + this.channels[channelType].activeItemId);
                        return;
                    }


                    resolve();

                    console.log('[' + channelType + '] Change ' + channelType + ' to ' + this.channels[channelType].activeItemId);
                });
            },

            updateChannelItem(channelType, id, update) {
                const itemIndex = this.channels[channelType].items.findIndex(item => item.id === id);

                if (itemIndex === -1) {
                    return;
                }

                this.channels[channelType].items[itemIndex] = {
                    ...this.channels[channelType].items[itemIndex],
                    ...update,
                };
            },

            /**
             * Remove item from channel
             *
             * @param channelType
             * @param id
             */
            removeChannelItem(channelType, id) {
                const item = this.channels[channelType].items.find(item => item.id === id);

                if (!item) {
                    return;
                }

                this.channels[channelType].items.splice(this.channels[channelType].items.indexOf(item), 1);
            },

            cleanupDurations() {
                // Cleanup not needed durations
                const durationsToClean = this.durations.filter(duration => {
                    const audioItemsForDuration = this.channels[CHANNELS.AUDIO].items.filter(item => item.durationId === duration.id);
                    const mediaItemsForDuration = this.channels[CHANNELS.MEDIA].items.filter(item => item.durationId === duration.id);

                    return !audioItemsForDuration.length && !mediaItemsForDuration.length;
                });

                for (const duration of durationsToClean) {
                    this.durations.splice(this.durations.indexOf(duration), 1);
                }
            },

            /**
             * Get the duration for the media to play
             *
             * @param block
             * @param type
             * @param file
             */
            getChannelItemDuration(block, type, file) {
                switch (type) {
                    case MEDIA_TYPES.VIDEO:
                        return Number(file.duration);
                    case MEDIA_TYPES.IMAGE:
                        return Number(getDurationPerImage(block, (block.original_files || block.files || []).filter(file => this.isAudio(file))));
                }

                return null;
            },

            /**
             * Get the media type
             *
             * @param block
             * @param file
             */
            getMediaType(block, file) {
                if (block.type === BLOCK_TYPES.CALL) {
                    return MEDIA_TYPES.LIVE;
                }

                if (this.isVideo(file)) {
                    return MEDIA_TYPES.VIDEO;
                } else if (this.isImage(file)) {
                    return MEDIA_TYPES.IMAGE;
                }

                return null;
            },

            /**
             * Restore the previous render state
             */
            restoreRenderState(defaultRenderState = RENDER_STATES.FULL) {
                this.renderState = this.lastRenderState || defaultRenderState;
                this.executeChannelItemMethod('setRenderState', [CHANNELS.AUDIO, CHANNELS.MEDIA], [{
                    renderState: this.renderState,
                }]);
            },

            /**
             * Set the render state
             *
             * @param renderState
             */
            setRenderState(renderState) {
                console.log('[general] Changed render state', renderState);
                this.lastRenderState = this.renderState;
                this.renderState = renderState;
                this.executeChannelItemMethod('setRenderState', [CHANNELS.AUDIO, CHANNELS.MEDIA], [{
                    renderState: this.renderState,
                }]);
            },

            setDurationState(durationId, durationState) {
                const duration = this.durations.find(duration => duration.id === durationId);

                if (duration) {
                    duration.state = durationState;
                    console.log('[general] Set duration ' + durationId + ' state to ' + durationState);
                }
            },

            /**
             * Handle the change events
             *
             * @param event
             */
            blockStateChangedEvent(event) {
                console.log('[general] Changed state', event.state);

                switch (event.state) {
                    case 'stopped':
                        this.stop(true);
                        break;
                    case 'paused':
                        this.pause();
                        break;
                    case 'playing':
                        this.play();
                        break;
                    case 'duration':
                        this.setDuration(event.duration || 0);
                        break;
                }
            },

            /**
             * Set the duration
             *
             * @param duration
             */
            setDuration(duration) {
                const durationState = this.durations.find(duration => duration.id === this.currentDurationId);

                if (durationState) {
                    durationState.progressBar.milliSeconds = duration;
                    durationState.progressBar.seconds = duration;
                }

                this.setDurationForChannel(duration, CHANNELS.MEDIA);
                this.setDurationForChannel(duration, CHANNELS.AUDIO);
            },

            setDurationForChannel(duration, channelType) {
                const items = this.channels[channelType].items;

                if (!items.length) {
                    return;
                }

                let currentIndex = 0;
                let countedDuration = 0;

                while (true) {
                    const item = items[currentIndex];
                    countedDuration += item.duration.total;

                    if (duration < countedDuration) {
                        this.gotoChannelItem(channelType, item.id, false, null, duration - (countedDuration - item.duration.total));
                        break;
                    }

                    currentIndex++;

                    if (currentIndex >= items.length) {
                        currentIndex = 0;
                    }
                }
            },

            /**
             * Handle the active block
             *
             * @param event
             */
            async blockActiveFileChangedEvent(event) {
                console.log('[general] Changed active file', event.file.id, event.file.name);

                // No media active yet, we need to start the block
                if (this.activeMediaBlock?.id !== event.block.id) {
                    await this.blockStartedEvent(event);

                    return;
                }

                // There is active media, just update the active item
                const mediaItem = this.channels[CHANNELS.MEDIA].items.find(item => {
                    return item.block.id === event.block.id && item.fileId === event.file.id;
                });

                if (mediaItem) {
                    this.gotoChannelItem(CHANNELS.MEDIA, mediaItem.id);
                }
            },

            /**
             * Ceremony has finished
             */
            ceremonyFinishedEvent() {
                console.log('[general] Ceremony finished');
                this.stop();
            },

            /**
             * Update the channel items for the given channel
             *
             * @param items
             * @param channel
             */
            setChannelItems(items, channel) {
                if (!this.channels[channel].items.length) {
                    this.channels[channel].items = [...items];

                    return;
                }

                // Build the new media
                const updatedItems = [...items];
                const activeItemIndex = this.channels[channel].items.findIndex(item => item.id === this.channels[channel].activeItemId);

                if (activeItemIndex !== -1) {
                    this.channels[channel].items[activeItemIndex].removeAfter = true;
                    updatedItems.unshift(this.channels[channel].items[activeItemIndex]);
                }

                this.channels[channel].items = updatedItems;
            },

            /**
             * Play the player
             */
            play(setDurationState = true, renderState = RENDER_STATES.FULL) {
                console.log('[general] Playing player');

                if (setDurationState) {
                    this.setDurationState(this.currentDurationId, DURATION_STATES.ACTIVE);
                }

                this.setRenderState(renderState);
            },

            /**
             * Pause the player
             */
            pause() {
                console.log('[general] Pausing player');

                this.setDurationState(this.currentDurationId, DURATION_STATES.IN_ACTIVE);

                this.setRenderState(RENDER_STATES.MEDIA_ONLY);
            },

            /**
             * Stop the player
             *
             * @param force
             */
            async stop(force = false) {
                console.log('[general] Stopping player' + (force ? ' with force' : ''));
                this.setDurationState(this.currentDurationId, DURATION_STATES.IN_ACTIVE);

                // we want to hide the last picture when we stop on purpose
                if (force) {
                    this.executeChannelItemMethod('setActiveItem', [CHANNELS.AUDIO, CHANNELS.MEDIA], [{ item: null }]);

                    // reset the channel items
                    this.channels[CHANNELS.AUDIO].items = [];
                    this.channels[CHANNELS.MEDIA].items = [];
                }
            },

            /**
             * Update the duration
             *
             * @param duration
             */
            async updateDuration(duration) {
                if (duration.state !== DURATION_STATES.ACTIVE) {
                    return;
                }

                console.log('[general] Duration', duration.progressBar.seconds, duration.progressBar.max);

                switch (this.communication) {
                    case PLAYER_COMMUNICATION.WEBSOCKET:
                        try {
                            const mediaChanged = this.channels[CHANNELS.MEDIA].lastItemId !== this.activeMedia?.id;
                            this.channels[CHANNELS.MEDIA].lastItemId = this.activeMedia?.id;

                            await this.$http.post('/api/player/blocks/duration-update', {
                                duration: duration.progressBar.seconds,
                                max_duration: duration.progressBar.max,
                                id: duration.block.id,
                                changed_file: mediaChanged,
                            });
                        } catch (error) {
                            console.error(error);
                        }

                        break;
                    case PLAYER_COMMUNICATION.IPC:
                    case PLAYER_COMMUNICATION.INLINE:
                        this.emitAction('BlockDurationChanged', {
                            duration: parseInt(duration.progressBar.seconds),
                            max_duration: parseInt(duration.progressBar.max),
                        });

                        break;
                }
            },

            async finishActiveBlock() {
                this.setDurationState(this.currentDurationId, DURATION_STATES.IN_ACTIVE);
                if (this.currentDuration) {
                    return this.finishBlock(this.currentDuration.block);
                }
            },

            /**
             * Mark the block as finished
             */
            async finishBlock(block) {
                console.log('[general] Mark block as finished', block.id, block.name, block);

                switch (this.communication) {
                    case PLAYER_COMMUNICATION.WEBSOCKET:
                        try {
                            await this.$http.post('/api/player/blocks/finished', {
                                id: block.id,
                            });
                        } catch (error) {
                            console.error(error);
                        }

                        break;
                    case PLAYER_COMMUNICATION.IPC:
                    case PLAYER_COMMUNICATION.INLINE:
                        this.emitAction('BlockFinished', {
                            block: {
                                ...block,
                            },
                        });

                        break;
                }
            },

            /**
             * Get the background for the given block
             *
             * @param block
             * @param channelItem
             */
            getBackground(block, channelItem) {
                const settings = block?.settings || [];
                const backgroundType = settings.find(setting => setting.name === 'background_type');

                switch (backgroundType?.value) {
                    case 'color':
                        // Color is set via the block
                        const backgroundColor = settings.find(setting => setting.name === 'background_color');

                        return {
                            type: BACKGROUND_TYPE.COLOR,
                            color: backgroundColor?.value || '#000000',
                        };
                    case 'photo_blur':
                        if (channelItem.src) {
                            return {
                                type: BACKGROUND_TYPE.DYNAMIC_BACKGROUND,
                                color: '#000000',
                                blur: true,
                                image: channelItem.src,
                            };
                        }

                        break;
                    case 'photo':
                        const backgroundPhotoSetting = settings.find(find_setting => find_setting.name === 'background_photo');

                        if (backgroundPhotoSetting) {
                            const backgroundPhoto = JSON.parse(backgroundPhotoSetting.value);
                            const url = backgroundPhoto.public_path.replace(/\/:code\//g, '/' + block.ceremony.code + '/');

                            return {
                                type: BACKGROUND_TYPE.IMAGE,
                                color: '#000000',
                                blur: true,
                                image: url,
                            };
                        }

                        return {
                            type: BACKGROUND_TYPE.COLOR,
                            color: '#000000',
                        };
                }

                // When we have no settings just the default color
                return {
                    type: BACKGROUND_TYPE.COLOR,
                    color: '#000000',
                };
            },

            transitionEndEnded(channelType) {
                const channel = this.channels[channelType];

                channel.transitioning = false;
            },

            /**
             * Handle the transition start event
             *
             * @param channelType
             * @param id
             */
            transitionStartStarted(channelType, id) {
                const channel = this.channels[channelType];
                const item = channel.items.find(item => item.id === id);

                if (item) {
                    this.setDurationState(item.durationId, DURATION_STATES.ACTIVE);
                    item.duration.timerState = TIMER_STATES.START;
                }
            },

            /**
             * Handle the transition ended event
             *
             * @param channelType
             * @param id
             */
            transitionChangedFinished(channelType, id) {
                const channel = this.channels[channelType];
                const item = channel.items.find(item => item.id === id);

                if (item) {
                    this.setDurationState(this.currentDurationId, DURATION_STATES.ACTIVE);
                }

                if (item && item.removeAfter) {
                    this.removeChannelItem(channelType, item.id);
                }
            },

            /**
             * Convert the object to a better object for debugging
             *
             * @param item
             */
            debugItem(item) {
                return {
                    ...item,
                    block: undefined,
                    debugBlockId: item.block.id,
                };
            },

            /**
             * Convert the array to a better array with objects for debugging
             *
             * @param durations
             */
            debugDurations(durations) {
                return durations.map(duration => {
                    return {
                        ...duration,
                        block: undefined,
                        debugBlockId: duration.block.id,
                    };
                });
            },

            executeChannelItemMethod(method, channels, methodArguments) {
                if (this.debug && method !== 'resizeInterval') {
                    this.debugData.commands.unshift({
                        time: new Date(),
                        method,
                        channels,
                    });
                }

                for (const channel of channels) {
                    if (!this.$refs['channel_' + channel]) {
                        continue;
                    }

                    this.$refs['channel_' + channel][method](...(methodArguments || []));
                }
            },

            limitTransitionTimeToDuration(transitionDuration, totalDuration, thresholdPercentage) {
                const transitionDurationPercentage = transitionDuration / (totalDuration / 100);

                // Transition percentage fits withing the threshold of the total duration
                if (transitionDurationPercentage < thresholdPercentage) {
                    return transitionDuration;
                }

                // Calculate new transition duration based on the total and threshold percentage
                return Math.round((totalDuration / 100) * thresholdPercentage);
            },

            getTransitionType(transitionType) {
                if (TRANSITIONS.hasOwnProperty(transitionType)) {
                    return TRANSITIONS[transitionType];
                }

                return TRANSITIONS[TRANSITION_TYPE.CROSS_FADE];
            },

            volumeUpdated(event) {
                console.log('[general] Set volume to ' + event.volume);

                if (window.setVolume) {
                    window.setVolume(event.volume);
                }
            },

            emitAction(action, data) {
                if (this.readonly) {
                    return;
                }

                if (this.communication === PLAYER_COMMUNICATION.INLINE) {
                    EventBus.$emit(action, data);
                } else if (this.communication === PLAYER_COMMUNICATION.IPC) {
                    window.dispatchEvent(new CustomEvent('ipc-to-controller', {
                        detail: {
                            action,
                            data,
                        },
                    }));
                }
            },

            getUniqueId(prefix) {
                return `${prefix}_${this.latestId++}`;
            },
        },

        beforeDestroy() {
            if (this.communication === PLAYER_COMMUNICATION.WEBSOCKET) {
                const channel = Echo.channel('blockmessages');

                channel.stopListening('BlockStarted');
                channel.stopListening('BlockStateChanged');
                channel.stopListening('CeremonyFinished');
            }

            if (this.communication === PLAYER_COMMUNICATION.IPC) {
                window.removeEventListener('ipc-from-controller', this.ipcFromControllerEvents);
            }

            if (this.durationIntervalId) {
                clearInterval(this.durationIntervalId);
            }
        },
    };

</script>
<style lang="scss" scoped>

    .fade-enter, .fade-leave-to {
        opacity: 0;
    }

    .main {
        height: 100vh;
        width: 100vw;
        cursor: none;
        pointer-events: none;
        z-index: 1;

        &.is-inline {
            width: 100%;
            height: 100%;
        }

        .background {
            &,
            & > * {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: -1;
            }

            .background-image {
                background-color: #000000;
                background-position: center;
                background-repeat: no-repeat;
                background-size: cover;

                &.blurred {
                    filter: blur(20px);
                }
            }
        }
    }

    .content-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        position: relative;
        overflow: hidden;

        > span {
            width: 100%;
        }
    }

    .fixed-image {
        background-position: center center;
        background-size: contain;
        background-repeat: no-repeat;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
    }

    .fade-fixed-image-enter, .fade-fixed-image-leave-to {
        opacity: 0;
    }

</style>
